import { Box, Chip, Divider, Skeleton, Stack, Theme, Typography, useTheme } from "@mui/material";
import { Avatar, BlockLink } from "../../../../components";
import { Links, TeamNetworkListObjectType } from "../../../../constants";
import { useGetTeamNetwork } from "../../../../http";
import { ITeamNetworkMember, ITeamNetworkRole, ITeamNetworkTeam } from "../../../../models";
import { TeamNetworkRoleListItem } from "./TeamNetworkRoleListItem";

interface ITeamNetworkObjectFilterOption {
  label: string;
  value: TeamNetworkListObjectType;
}

interface ITeamNetworkObject {
  type: TeamNetworkListObjectType;
  title: string;
  render: () => React.ReactNode;
}

interface IProps {
  teamSlug: string | undefined;
  filter?: (value: ITeamNetworkObject) => boolean;
}

const TeamNetworkList = (props: IProps) => {
  const { isLoading: isLoadingNetworkList, isError, data } = useGetTeamNetwork(props.teamSlug);
  const theme = useTheme();

  if (props.teamSlug === undefined || isLoadingNetworkList) {
    return (
      <Box sx={{ p: 1 }}>
        <Stack spacing={1}>
          {[...Array(2)].map((_, i) => (
            <Stack key={i} spacing={0.25}>
              <Skeleton variant="rectangular" height="25px" width="60px" />
              <Skeleton variant="rectangular" height="30px" width="200px" />
            </Stack>
          ))}
        </Stack>
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography variant="body1" sx={{ p: 1 }}>
        Couldn't load network.
      </Typography>
    );
  }

  const networkObjects: ITeamNetworkObject[] = [];
  networkObjects.push(...data.childTeams.map((x) => mapTeamToTeamNetworkObject(x, theme)));
  networkObjects.push(...data.roles.map((x) => mapRoleToTeamNetworkObject(x)));
  networkObjects.push(...data.members.map((x) => mapMemberToTeamNetworkObject(x, theme)));

  if (networkObjects.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body1">Nothing here yet.</Typography>
      </Box>
    );
  }

  return (
    <>
      {data && (
        <Stack divider={<Divider />} sx={{ overflowY: "auto" }}>
          {networkObjects
            .filter(props.filter ?? (() => true))
            .sort((a, b) => {
              if (a.type === b.type) {
                return a.title.localeCompare(b.title);
              }

              return a.type < b.type ? -1 : 1;
            })
            .map((x, i) => (
              <Box key={i}>{x.render()}</Box>
            ))}
        </Stack>
      )}
    </>
  );
};

function mapTeamToTeamNetworkObject(team: ITeamNetworkTeam, theme: Theme): ITeamNetworkObject {
  return {
    type: TeamNetworkListObjectType.Team,
    title: team.name,
    render: () => (
      <BlockLink
        href={Links.Team(team.slug)}
        backgroundColorOnHover={theme.palette.primary.transparent5}
        sx={{ p: { xs: 0.5, sm: 1 } }}
      >
        <Stack>
          <Chip label="Team" color="primary" sx={{ mb: 0.5 }} />
          <Typography variant="h5">{team.name}</Typography>
          {team.description && <Typography variant="body1">{team.description}</Typography>}
        </Stack>
      </BlockLink>
    ),
  };
}

function mapRoleToTeamNetworkObject(role: ITeamNetworkRole): ITeamNetworkObject {
  return {
    type: TeamNetworkListObjectType.Role,
    title: role.name,
    render: () => <TeamNetworkRoleListItem role={role} />,
  };
}

function mapMemberToTeamNetworkObject(member: ITeamNetworkMember, theme: Theme): ITeamNetworkObject {
  return {
    type: TeamNetworkListObjectType.Member,
    title: member.user.displayName,
    render: () => (
      <BlockLink
        href={Links.User(member.user.userId)}
        backgroundColorOnHover={theme.palette.primary.transparent5}
        sx={{ p: { xs: 0.5, sm: 1 } }}
      >
        <Stack>
          <Chip label="Person" color="person" sx={{ mb: 0.5 }} />
          <Stack direction="row" spacing={0.5}>
            <Avatar user={member.user} border />
            <Box>
              <Typography variant="h5">{member.user.displayName}</Typography>
              <Typography variant="body1">{member.roleNames.join(", ")}</Typography>
            </Box>
          </Stack>
        </Stack>
      </BlockLink>
    ),
  };
}

export { TeamNetworkList, TeamNetworkListObjectType };
export type { ITeamNetworkObjectFilterOption };
